import {
  DEFAULT_RUN_SETTINGS,
  serializeRunSettings,
  type RunSettings,
} from "@/lib/run-settings";
import type { Setter } from "jotai";
import type { Edge } from "@xyflow/react";
import { MarkerType } from "@xyflow/react";
import { v4 as uuidv4 } from "uuid";
import type { NodeDefinition } from "@/generated";
import type {
  GroupNodeV2ReactflowNode,
  NoxusReactflowNodeV2,
  StickyNoteNodeV2ReactflowNode,
  WorkflowDefinitionV2,
} from "@/types/spotflow-v2";
import {
  editorRevisionAtom,
  hasQueueToSaveAtom,
  hasUnsavedUserEditAtom,
  saveStateAtom,
} from "./atoms";
import { serializeConfigForBackend } from "@/components/Form/RichTextField";
import {
  isMergePathsNode,
  mergeInputHandlesFromConfig,
} from "@/components/SpotflowV2/Sidebar/smartRouteBindings";
import {
  isFlowInputNode,
  isFlowOutputNode,
  SINK_ONLY_TYPES,
  SOURCE_ONLY_TYPES,
} from "@/components/SpotflowV2/constants";
import {
  parseVarRefId,
  remapVariableBindingRefs,
  remapVarRefsInConfig,
} from "@/components/SpotflowV2/Sidebar/variableRefs";
import { resolvePluginNodeDisplayName } from "@/utils/pluginNodeDisplay";

const STICKY_NOTE_DEFAULT_WIDTH = 280;
const STICKY_NOTE_DEFAULT_HEIGHT = 200;
const STICKY_NOTE_DEFAULT_COLOR = "yellow";

export const STICKY_NOTE_DEFINITION_TYPE = "StickyNoteNode" as const;
export const GROUP_DEFINITION_TYPE = "GroupNode" as const;

const LEGACY_STICKY_NOTE_DEFINITION_TYPES = new Set([
  "note",
  STICKY_NOTE_DEFINITION_TYPE,
]);

const LEGACY_GROUP_DEFINITION_TYPES = new Set([
  "groupNodeV2",
  GROUP_DEFINITION_TYPE,
]);

export function isStickyNoteDefinitionType(type: string): boolean {
  return LEGACY_STICKY_NOTE_DEFINITION_TYPES.has(type);
}

export function isGroupDefinitionType(type: string): boolean {
  return LEGACY_GROUP_DEFINITION_TYPES.has(type);
}

export function isStickyNoteReactFlowNode(node: {
  type?: string;
  data?: { nodeType?: string };
}): boolean {
  return (
    node.type === "note" ||
    isStickyNoteDefinitionType(node.data?.nodeType ?? "")
  );
}

/** Groups and sticky notes are canvas-only: they run nothing, have no config
 *  and render no handles. */
export function isCanvasOnlyReactFlowNode(node: {
  type?: string;
  data?: { nodeType?: string };
}): boolean {
  return (
    isGroupDefinitionType(node.type ?? "") || isStickyNoteReactFlowNode(node)
  );
}

// ── Mark dirty (replaces 10+ copy-pasted blocks) ─────────────────────────

// "derived": a change the editor makes on its own (an auto-layout, iterate
// flags), which a newer version of the flow re-derives, so it may be dropped.
export type DirtyOrigin = "user" | "derived";

export function markDirty(set: Setter, origin: DirtyOrigin = "user") {
  set(editorRevisionAtom, (revision) => revision + 1);
  set(hasQueueToSaveAtom, true);
  if (origin === "user") set(hasUnsavedUserEditAtom, true);
  set(saveStateAtom, {
    timestamp: new Date().toISOString(),
    status: "unsaved_changes",
  });
}

// ── Display-name helpers (used in history labels) ────────────────────────

/** Best human-readable identifier for a node — the editable name first,
 *  then the registry title, then the raw nodeType, then a generic fallback. */
export function getNodeDisplayName(
  node: NoxusReactflowNodeV2 | undefined
): string {
  if (!node) return "node";
  const data = node.data;
  const name = typeof data?.name === "string" ? data.name.trim() : "";
  if (name) return name;
  const title = typeof data?.title === "string" ? data.title.trim() : "";
  if (title) return title;
  return data?.nodeType || "node";
}

export function findNodeDisplayName(
  nodes: ReadonlyArray<NoxusReactflowNodeV2>,
  id: string
): string {
  return getNodeDisplayName(nodes.find((n) => n.id === id));
}

// ── Flow Input/Output name uniqueness ───────────────────────────────────────
// Flow Input and Output node names double as the workflow's input/output keys,
// so they must not collide. Uniqueness is enforced *per kind*: an Input named
// "foo" never clashes with an Output named "foo", but two Inputs can't share a
// name (and neither can two Outputs).

function isSameFlowIOKind(
  a: string | undefined,
  b: string | undefined
): boolean {
  if (isFlowInputNode(a)) return isFlowInputNode(b);
  if (isFlowOutputNode(a)) return isFlowOutputNode(b);
  return false;
}

/** Names already taken by flow I/O nodes of the same kind as `nodeType`,
 *  excluding `excludeId` (the node being renamed). */
function takenFlowIONames(
  nodes: ReadonlyArray<NoxusReactflowNodeV2>,
  nodeType: string,
  excludeId?: string
): Set<string> {
  const taken = new Set<string>();
  for (const n of nodes) {
    if (n.id === excludeId) continue;
    if (!isSameFlowIOKind(nodeType, n.data.nodeType)) continue;
    if (typeof n.data.name === "string") taken.add(n.data.name);
  }
  return taken;
}

/** Default name for a freshly-added flow I/O node: "Input N" / "Output N"
 *  with the smallest N that isn't already used by a same-kind node. Scans
 *  existing names rather than counting, so delete + re-add can't regenerate a
 *  name that's still in use. */
export function nextFlowIODefaultName(
  nodes: ReadonlyArray<NoxusReactflowNodeV2>,
  nodeType: string
): string {
  const base = isFlowInputNode(nodeType) ? "Input" : "Output";
  const taken = takenFlowIONames(nodes, nodeType);
  let i = 1;
  while (taken.has(`${base} ${i}`)) i++;
  return `${base} ${i}`;
}

/** Make `desired` unique among same-kind flow I/O nodes by appending
 *  " (1)", " (2)"… on collision. `excludeId` is the node being renamed so it
 *  doesn't collide with itself (and avoids double-suffixing). */
export function uniqueFlowIOName(
  nodes: ReadonlyArray<NoxusReactflowNodeV2>,
  nodeType: string,
  desired: string,
  excludeId?: string
): string {
  const taken = takenFlowIONames(nodes, nodeType, excludeId);
  if (!taken.has(desired)) return desired;
  let i = 1;
  while (taken.has(`${desired} (${i})`)) i++;
  return `${desired} (${i})`;
}

// ── Edge factory ──────────────────────────────────────────────────────────

export function createEdge(opts: {
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
}): Edge {
  return {
    id: uuidv4(),
    source: opts.source,
    target: opts.target,
    sourceHandle: opts.sourceHandle,
    targetHandle: opts.targetHandle ?? "input",
    type: "v2Edge",
    markerEnd: { type: MarkerType.ArrowClosed },
  };
}

// ── Node definition helpers ───────────────────────────────────────────────

// `output_paths` / `input_paths` ship from the backend on NodeDefinition but
// are missing from the current generated types — read them off a structural
// view until the OpenAPI spec is regenerated.
type NodeDefinitionPaths = {
  output_paths?: string[];
  input_paths?: string[];
};

/**
 * Derive output handles from registry node definition.
 * Only routing nodes (conditionals, smart route, etc.) have named output paths.
 */
export function getOutputHandles(nodeDefinition: NodeDefinition): string[] {
  const paths = (nodeDefinition as NodeDefinitionPaths).output_paths;
  return paths && paths.length > 0 ? paths : [];
}

export function getInputHandles(nodeDefinition: NodeDefinition): string[] {
  const paths = (nodeDefinition as NodeDefinitionPaths).input_paths;
  return paths && paths.length > 0 ? paths : [];
}

/**
 * Resolve an edge's target handle against the target node's named input
 * handles. Nodes with `input_paths` (Merge Paths, …) render per-path handles
 * and NO generic "input" handle — an edge stored with "input" cannot attach,
 * so React Flow silently drops it (error 008). Missing/generic handles map to
 * the first path not already taken, so consecutive edges fill path_a, path_b…
 */
export function resolveTargetHandle(opts: {
  requested?: string | null;
  namedHandles: string[];
  takenHandles: ReadonlySet<string>;
}): string | undefined {
  const { requested, namedHandles, takenHandles } = opts;
  if (requested && requested !== "input") return requested;
  if (namedHandles.length === 0) return requested ?? undefined;
  return namedHandles.find((h) => !takenHandles.has(h)) ?? namedHandles[0];
}

/** Definition → React Flow edges, remapping generic "input" target handles
 * onto named input handles (see `resolveTargetHandle`). Explicit named
 * handles are reserved first so remapped edges never collide with them.
 *
 * Edges landing on a side the node renders no handle for (into an Input node,
 * out of an Output node) are dropped: React Flow can't draw them, so they were
 * invisible and undeletable, yet the engine still counted them as dependencies
 * — one into an Input node was enough to fail every run with "loop detected".
 * Dropping them here self-heals flows saved before the connect guard landed. */
export function hydrateDefinitionEdges(
  definition: {
    nodes: Array<{
      id: string;
      type: string;
      node_config?: Record<string, unknown> | null;
    }>;
    edges: Array<{
      id: string;
      source: string;
      target: string;
      source_handle?: string | null;
      target_handle?: string | null;
    }>;
  },
  nodeRegistry: NodeDefinition[]
): Edge[] {
  const namedByNode = new Map<string, string[]>(
    definition.nodes.map((n) => {
      // Dynamic-input nodes (Merge Paths) carry their handles in config rows;
      // the registry only knows the legacy fixed fallback.
      if (isMergePathsNode(n.type)) {
        return [
          n.id,
          mergeInputHandlesFromConfig(n.node_config ?? undefined).map(
            (h) => h.id
          ),
        ];
      }
      const registryNode = nodeRegistry.find((r) => r.type === n.type);
      return [n.id, registryNode ? getInputHandles(registryNode) : []];
    })
  );
  const typeByNode = new Map(definition.nodes.map((n) => [n.id, n.type]));

  // Nodes that are not attachable to edges
  const canvasOnly = (nodeId: string): boolean => {
    const type = typeByNode.get(nodeId) ?? "";
    return isGroupDefinitionType(type) || isStickyNoteDefinitionType(type);
  };
  const attachable = (e: { source: string; target: string }): boolean =>
    !SOURCE_ONLY_TYPES.has(typeByNode.get(e.target) ?? "") &&
    !SINK_ONLY_TYPES.has(typeByNode.get(e.source) ?? "") &&
    !canvasOnly(e.source) &&
    !canvasOnly(e.target);
  const definitionEdges = definition.edges.filter(attachable);
  const takenByTarget = new Map<string, Set<string>>();
  const taken = (target: string): Set<string> => {
    let s = takenByTarget.get(target);
    if (!s) {
      s = new Set();
      takenByTarget.set(target, s);
    }
    return s;
  };
  for (const e of definitionEdges) {
    if (e.target_handle && e.target_handle !== "input") {
      taken(e.target).add(e.target_handle);
    }
  }
  return definitionEdges.map((e) => {
    const targetHandle =
      resolveTargetHandle({
        requested: e.target_handle,
        namedHandles: namedByNode.get(e.target) ?? [],
        takenHandles: taken(e.target),
      }) ?? "input";
    taken(e.target).add(targetHandle);
    return {
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.source_handle ?? undefined,
      targetHandle,
      type: "v2Edge",
      markerEnd: { type: MarkerType.ArrowClosed },
    };
  });
}

export function normalizeStickyNoteNodeConfig(
  nodeConfig: unknown
): Record<string, unknown> {
  if (typeof nodeConfig === "string") {
    return {
      color: STICKY_NOTE_DEFAULT_COLOR,
      text: nodeConfig,
      definition: null,
    };
  }

  if (
    !nodeConfig ||
    typeof nodeConfig !== "object" ||
    Array.isArray(nodeConfig)
  ) {
    return {
      color: STICKY_NOTE_DEFAULT_COLOR,
      text: "",
      definition: null,
    };
  }

  const normalized = { ...(nodeConfig as Record<string, unknown>) };

  normalized.color =
    typeof normalized.color === "string"
      ? normalized.color
      : STICKY_NOTE_DEFAULT_COLOR;
  normalized.text = typeof normalized.text === "string" ? normalized.text : "";
  normalized.definition =
    normalized.definition &&
    typeof normalized.definition === "object" &&
    !Array.isArray(normalized.definition)
      ? normalized.definition
      : null;

  return normalized;
}

function parseDimension(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return undefined;
}

function getStickyNoteSize(node: NoxusReactflowNodeV2): {
  width: number;
  height: number;
} {
  const width =
    parseDimension(node.width) ??
    parseDimension(node.measured?.width) ??
    parseDimension(node.style?.width) ??
    STICKY_NOTE_DEFAULT_WIDTH;
  const height =
    parseDimension(node.height) ??
    parseDimension(node.measured?.height) ??
    parseDimension(node.style?.height) ??
    STICKY_NOTE_DEFAULT_HEIGHT;

  return { width, height };
}

function finitePosition(position: { x: number; y: number }): {
  x: number;
  y: number;
} {
  return {
    x: Number.isFinite(position.x) ? position.x : 0,
    y: Number.isFinite(position.y) ? position.y : 0,
  };
}

export const STICKY_NOTE_VIEWPORT_JITTER_PX = 20;

export function getStickyNoteViewportPosition(args: {
  paneRect: DOMRect;
  screenToFlowPosition: (screenPosition: { x: number; y: number }) => {
    x: number;
    y: number;
  };
}): { x: number; y: number } {
  const jitter = () =>
    Math.floor((Math.random() - 0.5) * STICKY_NOTE_VIEWPORT_JITTER_PX * 2);

  const flowCenter = args.screenToFlowPosition({
    x: args.paneRect.left + args.paneRect.width / 2 + jitter(),
    y: args.paneRect.top + args.paneRect.height / 2 + jitter(),
  });

  return {
    x: flowCenter.x - STICKY_NOTE_DEFAULT_WIDTH / 2,
    y: flowCenter.y - STICKY_NOTE_DEFAULT_HEIGHT / 2,
  };
}

/** Canonical React Flow node for a V2 sticky note — used by load and add paths. */
export function createStickyNoteReactFlowNode(args: {
  id?: string;
  position: { x: number; y: number };
  nodeConfig?: unknown;
  size?: { width?: number; height?: number };
}): StickyNoteNodeV2ReactflowNode {
  const size = {
    width: args.size?.width ?? STICKY_NOTE_DEFAULT_WIDTH,
    height: args.size?.height ?? STICKY_NOTE_DEFAULT_HEIGHT,
  };
  const normalizedNoteConfig = normalizeStickyNoteNodeConfig(args.nodeConfig);

  return {
    id: args.id ?? uuidv4(),
    type: "note",
    dragHandle: ".sticky-note-drag-handle",
    position: finitePosition(args.position),
    deletable: true,
    width: size.width,
    height: size.height,
    measured: { width: size.width, height: size.height },
    style: {
      width: size.width,
      height: size.height,
      zIndex: -2,
    },
    data: {
      nodeType: STICKY_NOTE_DEFINITION_TYPE,
      name: "Note",
      nodeConfig: normalizedNoteConfig,
      variableBindings: {},
      outputHandles: [],
      inputHandles: [],
      pinnedFields: [],
      iterate: false,
      iterateOverrides: {},
      title: "Note",
      color: "#fbbf24",
      deletable: true,
    },
  };
}

export function createGroupReactFlowNode(args: {
  id?: string;
  position: { x: number; y: number };
  size?: { width?: number; height?: number };
  nodeConfig?: Record<string, unknown>;
  name?: string;
}): GroupNodeV2ReactflowNode {
  const width = args.size?.width ?? 400;
  const height = args.size?.height ?? 300;
  const nodeConfig = args.nodeConfig ?? {};

  return {
    id: args.id ?? uuidv4(),
    type: "groupNodeV2",
    position: finitePosition(args.position),
    width,
    height,
    style: { width, height, zIndex: -1 },
    deletable: true,
    data: {
      nodeType: GROUP_DEFINITION_TYPE,
      name: args.name ?? "Group",
      nodeConfig,
      variableBindings: {},
      outputHandles: [],
      inputHandles: [],
      pinnedFields: [],
      iterate: false,
      iterateOverrides: {},
      title: "Group",
      color: typeof nodeConfig.color === "string" ? nodeConfig.color : "blue",
      deletable: true,
      isGroup: true,
    },
  };
}

type RegistryConfigField = {
  default?: unknown;
  pinned?: boolean;
  visible?: boolean;
};

function isConfigField(value: unknown): value is RegistryConfigField {
  return typeof value === "object" && value !== null;
}

/** Extract `Parameter(default=...)` values from a node registry entry. */
export function getRegistryConfigDefaults(
  registryNode: NodeDefinition | undefined
): Record<string, unknown> {
  const config = (registryNode?.config ?? {}) as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(config)) {
    if (key.startsWith("_")) continue; // skip private keys (_credential_id_*, etc.)
    if (!isConfigField(value)) continue;
    if (value.default !== undefined) out[key] = value.default;
  }
  // Smart Route's `routes` field is `default_factory`, so it has no static
  // `default` to copy above. Seed two routes with freshly minted stable ids
  // here so the node shows its two output connections immediately on add (the
  // ids are what edges bind to). Seed real names too — handle labels derive
  // from the route `name`, so an empty name would render the raw UUID until the
  // config editor backfills it.
  if (registryNode?.type === "SmartRouteV2Node" && !Array.isArray(out.routes)) {
    out.routes = ["New Route", "New Route 2"].map((name) => ({
      id: uuidv4(),
      name,
      condition: "",
    }));
  }
  if (registryNode?.type === "SwitchV2Node" && !Array.isArray(out.branches)) {
    const usedNames: string[] = [];
    out.branches = [0, 1].map(() => {
      let name = "New path";
      if (usedNames.includes(name)) {
        let suffix = 2;
        while (usedNames.includes(`New path ${suffix}`)) {
          suffix += 1;
        }
        name = `New path ${suffix}`;
      }
      usedNames.push(name);
      return {
        id: uuidv4(),
        name,
        conditions: [
          {
            logic: "OR",
            condition_type: "REGULAR",
            regular_condition: {
              field_type: "TEXT",
              field: "",
              condition: "CONTAINS",
              value: "",
            },
            ai_condition: { prompt: "" },
          },
        ],
      };
    });
  }
  return out;
}

/** Extract field names declared `Parameter(pinned=True)` in a node registry entry. */
export function getRegistryPinnedFields(
  registryNode: NodeDefinition | undefined
): string[] {
  return registryNode?.pinned_fields ?? [];
}

/** Convert a V2 definition node + registry metadata -> React Flow node */
export function definitionNodeToReactFlow(
  defNode: WorkflowDefinitionV2["nodes"][number],
  registryNode: NodeDefinition | undefined
): NoxusReactflowNodeV2 {
  if (isStickyNoteDefinitionType(defNode.type)) {
    return createStickyNoteReactFlowNode({
      id: defNode.id,
      position: {
        x: defNode.display.position.x,
        y: defNode.display.position.y,
      },
      nodeConfig: defNode.node_config,
      size: defNode.display.size,
    }) as unknown as NoxusReactflowNodeV2;
  }

  if (isGroupDefinitionType(defNode.type)) {
    return createGroupReactFlowNode({
      id: defNode.id,
      position: {
        x: defNode.display.position.x,
        y: defNode.display.position.y,
      },
      nodeConfig: defNode.node_config,
      size: defNode.display.size,
      name: defNode.name ?? undefined,
    }) as unknown as NoxusReactflowNodeV2;
  }

  const outputHandles = registryNode ? getOutputHandles(registryNode) : [];
  const inputHandles = registryNode ? getInputHandles(registryNode) : [];

  const defaultName = defNode.name ?? registryNode?.title ?? defNode.type;
  const displayName =
    resolvePluginNodeDisplayName({
      nodeType: defNode.type,
      nodeConfig: defNode.node_config,
      currentName: defaultName,
      registryTitle: registryNode?.title,
    }) ?? defaultName;

  return {
    id: defNode.id,
    type: "baseNodeV2",
    position: {
      x: defNode.display.position.x,
      y: defNode.display.position.y,
    },
    parentId: defNode.parent_id ?? undefined,
    // A parented node is constrained to its container so it can't be dragged
    // out (V1 group semantics — "stuck at the edges"). Pure visual containment.
    ...(defNode.parent_id ? { extent: "parent" as const } : {}),
    deletable: registryNode?.deletable ?? true,
    data: {
      nodeType: defNode.type,
      name: displayName,
      nodeConfig: defNode.node_config,
      variableBindings: defNode.variable_bindings,
      outputHandles,
      inputHandles,
      pinnedFields: defNode.display?.pinned_fields ?? [],
      iterate: defNode.iterate ?? false,
      iterateOverrides: defNode.iterate_overrides ?? {},
      title: registryNode?.title ?? defNode.type,
      color:
        (registryNode as unknown as { color?: string })?.color ?? "#6366f1",
      image: registryNode?.image ?? undefined,
      deletable: registryNode?.deletable ?? true,
    },
  };
}

// ── Serialization ─────────────────────────────────────────────────────────

/**
 * Rewrite template mention tokens from the V2 binding ref to the connector
 * slot key.
 *
 * When a user inserts a variable into a rich-text template (ComposeTextNode's
 * `template`, an LLM prompt, …) the mention serializes as `((<nodeId>.output))`
 * — the raw binding ref (see `CustomMention` + `upstreamChipOptions`). But the
 * runtime nodes substitute by their connector *slot key*: `ComposeTextNode`
 * does `template.replace("((<key>))", value)` where `<key>` is e.g. `Input 1`,
 * the key the auto-bind reconciler assigned. So `((<ref>))` never matches and
 * the placeholder survives verbatim.
 *
 * The node's `variable_bindings` are exactly the ref→slot-key bridge
 * (`<connector>__<slotKey>` → `<ref>`), so on save we rewrite every `((<ref>))`
 * token to `((<slotKey>))`. The token then matches the connector key and the
 * substitution works. Idempotent: a token that's already a slot key isn't a
 * ref, so it's left alone.
 */
export function rewriteTemplateMentionRefs(
  nodeConfig: Record<string, unknown>,
  variableBindings: Record<string, string>
): Record<string, unknown> {
  const refToSlotKey = new Map<string, string>();
  for (const [bindKey, ref] of Object.entries(variableBindings)) {
    if (!ref) continue;
    const sep = bindKey.indexOf("__");
    if (sep < 0) continue; // direct connector binding — not a template slot
    const slotKey = bindKey.slice(sep + 2);
    if (slotKey && ref !== slotKey && !refToSlotKey.has(ref)) {
      refToSlotKey.set(ref, slotKey);
    }
  }
  if (refToSlotKey.size === 0) return nodeConfig;

  const rewriteString = (s: string): string => {
    let out = s;
    for (const [ref, slotKey] of refToSlotKey) {
      if (out.includes(`((${ref}))`)) {
        out = out.split(`((${ref}))`).join(`((${slotKey}))`);
      }
    }
    return out;
  };
  const walk = (value: unknown): unknown => {
    if (typeof value === "string") return rewriteString(value);
    if (Array.isArray(value)) return value.map(walk);
    if (value && typeof value === "object") {
      return Object.fromEntries(
        Object.entries(value).map(([k, v]) => [k, walk(v)])
      );
    }
    return value;
  };
  return walk(nodeConfig) as Record<string, unknown>;
}

/**
 * Drop top-level `null`/`undefined` config values before persisting. The config
 * form leaves a cleared or conditionally-hidden optional field as `null`, but a
 * `default_factory` field is rejected by `None` server-side — an *absent* key,
 * by contrast, falls back to its default. Only the top level is pruned so a
 * null *inside* a dict-row value (a legitimately empty row) is preserved.
 */
function dropNullConfigValues(
  config: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(config).filter(([, v]) => v !== null && v !== undefined)
  );
}

export function serializeDefinitionV2(
  nodes: NoxusReactflowNodeV2[],
  edges: Edge[],
  runSettings: RunSettings = DEFAULT_RUN_SETTINGS
): WorkflowDefinitionV2 {
  return {
    flow_version: "v2",
    ...serializeRunSettings(runSettings),
    nodes: nodes.map((n) => {
      const isStickyNote = isStickyNoteReactFlowNode(n);
      const stickySize = isStickyNote ? getStickyNoteSize(n) : undefined;
      const nodeConfig = isStickyNote
        ? normalizeStickyNoteNodeConfig(n.data.nodeConfig)
        : dropNullConfigValues(
            rewriteTemplateMentionRefs(
              serializeConfigForBackend(
                n.data.nodeConfig as Record<string, unknown>
              ),
              n.data.variableBindings ?? {}
            )
          );

      // Group frames persist their explicit size (the user resizes them and
      // they grow around members), so members land in the right place after
      // reload. Other nodes leave size off — react-flow measures them at
      // runtime.
      const containerSize =
        !isStickyNote && (n.width || n.height)
          ? {
              ...(n.width ? { width: n.width } : {}),
              ...(n.height ? { height: n.height } : {}),
            }
          : undefined;

      return {
        id: n.id,
        type: n.data.nodeType,
        name: n.data.name || null,
        node_config: nodeConfig,
        variable_bindings: n.data.variableBindings,
        ...(n.data.iterate ? { iterate: true } : {}),
        ...(n.data.iterateOverrides &&
        Object.keys(n.data.iterateOverrides).length > 0
          ? { iterate_overrides: n.data.iterateOverrides }
          : {}),
        ...(n.parentId ? { parent_id: n.parentId } : {}),
        display: {
          position: { x: n.position.x, y: n.position.y },
          ...(stickySize
            ? { size: stickySize }
            : containerSize
              ? { size: containerSize }
              : {}),
          ...(n.data.pinnedFields && n.data.pinnedFields.length > 0
            ? { pinned_fields: n.data.pinnedFields }
            : {}),
        },
      };
    }),
    edges: edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      source_handle: e.sourceHandle ?? null,
      // Persist the target handle so edges to named target handles
      // (loop's before_loop/end_loop, MergePaths' path_a/b, …) survive reload.
      target_handle: e.targetHandle ?? null,
    })),
  };
}

// ── Clipboard helpers ─────────────────────────────────────────────────────

export function remapNodeIds(
  nodes: NoxusReactflowNodeV2[],
  edges: Edge[]
): {
  nodes: NoxusReactflowNodeV2[];
  edges: Edge[];
  idMap: Map<string, string>;
} {
  const idMap = new Map<string, string>();
  for (const n of nodes) {
    idMap.set(n.id, uuidv4());
  }

  // Refs whose source wasn't copied (external upstream nodes, `__secrets__`)
  // miss the idMap and stay untouched.
  const remapRefSourceId = (refId: string): string | null => {
    const parsed = parseVarRefId(refId);
    if (!parsed) return null;
    const newSourceId = idMap.get(parsed.sourceNodeId);
    return newSourceId ? `${newSourceId}.${parsed.outputName}` : null;
  };

  const remappedNodes: NoxusReactflowNodeV2[] = nodes.map((n) => {
    const remappedParentId = n.parentId ? idMap.get(n.parentId) : undefined;
    const remapped: NoxusReactflowNodeV2 = {
      ...n,
      id: idMap.get(n.id) ?? uuidv4(),
      parentId: remappedParentId,
      selected: true,
      data: {
        ...n.data,
        nodeConfig: remapVarRefsInConfig(
          n.data.nodeConfig ?? {},
          remapRefSourceId
        ),
        variableBindings: remapVariableBindingRefs(
          n.data.variableBindings ?? {},
          remapRefSourceId
        ),
      },
    };
    // Stale clipboard payloads may reference a group that was never copied —
    // dropping parentId (and the extent pin) keeps the node from orphaning
    // under a group that doesn't exist in this flow.
    if (n.parentId && !remappedParentId) remapped.extent = undefined;
    return remapped;
  });

  // Only keep internal edges (both endpoints in the copied set)
  const copiedIds = new Set(nodes.map((n) => n.id));
  const remappedEdges: Edge[] = edges
    .filter((e) => copiedIds.has(e.source) && copiedIds.has(e.target))
    .map((e) => ({
      ...e,
      id: uuidv4(),
      source: idMap.get(e.source) ?? e.source,
      target: idMap.get(e.target) ?? e.target,
    }));

  return { nodes: remappedNodes, edges: remappedEdges, idMap };
}
